import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateUiState,
  updateOnboardingStepCompletionPercentage,
} from 'redux/slices/uiSlice';
// import { fetchPlaybookHistory } from 'redux/slices/miscSlice';
import {
  fetchOrganizationConfigs,
  getInteractionCounts,
} from 'redux/slices/orgSlice';
import { isOnboardingIncomplete } from '@/features/onboarding/onboardingConfig';

/**
 * Keeps the legacy onboarding checklist in sync with the org config: which of
 * its steps count as complete, and the per-step completion percentages that
 * live in ui state. Also mirrors `onboardingStage` /
 * `experimentalFeaturesEnabled` from the org config into ui state, and runs
 * the Dashboard's one mount-time bootstrap fetch.
 *
 * Split out of `Dashboard.js` in Phase 6. `Dashboard` calls this
 * unconditionally, so every effect keeps the lifetime it had inline.
 *
 * The `methodologiesAvailable` effect is declared immediately before the
 * percentage effect, exactly as it was inline: the percentage effect reads
 * `methodologiesAvailable`, and both are keyed on `[orgConfigs]`, so within a
 * commit the percentage effect sees the previous value. That ordering is
 * load-bearing and is preserved verbatim.
 */
const useOnboardingProgress = () => {
  const dispatch = useDispatch();
  const {
    showOnboardingDialog,
    onBoardingStep,
    showOnboardingDialogV2,
  } = useSelector((state) => state.uiStates);
  const userDetails = useSelector((state) => state.userDetails.userDetails);
  const { orgConfigs, fetchingFailed: orgConfigsFailed } = useSelector(
    (state) => state.org.orgConfigs,
  );
  const teamId = localStorage.getItem('teamId');

  // Read inside the config effect without making it a dependency: the effect
  // dispatches this flag, so keying on it would re-run the effect on its own
  // write.
  const showOnboardingDialogV2Ref = useRef(showOnboardingDialogV2);
  showOnboardingDialogV2Ref.current = showOnboardingDialogV2;

  const [, setCompletedStep] = useState({});
  const [methodologiesAvailable, setMethodologiesAvailable] = useState([]);
  const [, setSelectedPlaybook] = useState(null);

  useEffect(() => {
    if (showOnboardingDialog) {
      dispatch(fetchOrganizationConfigs());
    }
  }, [showOnboardingDialog]);

  // useEffect(() => {
  //   if (showOnboardingDialog && onBoardingStep === 1) {
  //     dispatch(fetchPlaybookHistory(teamId || null))
  //       .unwrap()
  //       .then((data) => {
  //         setSelectedPlaybook(data?.selectedPlaybook);
  //       })
  //       .catch(() => {});
  //   }
  // }, [showOnboardingDialog, onBoardingStep]);

  useEffect(() => {
    if (
      orgConfigs &&
      ((orgConfigs?.methodologies && orgConfigs?.methodologies?.length > 0) ||
        (orgConfigs?.customMethodologies &&
          orgConfigs?.customMethodologies?.length > 0))
    ) {
      setMethodologiesAvailable(true);
    } else {
      setMethodologiesAvailable(false);
    }
  }, [orgConfigs]);

  useEffect(() => {
    dispatch(
      updateOnboardingStepCompletionPercentage({
        key: 1,
        value: Math.floor(
          (orgConfigs?.botVerticals &&
          Array.isArray(orgConfigs?.botVerticals) &&
          orgConfigs?.botVerticals.length > 0
            ? 12.5
            : 0) +
            (orgConfigs?.botDesignations &&
            Array.isArray(orgConfigs?.botDesignations) &&
            orgConfigs?.botDesignations.length > 0
              ? 12.5
              : 0),
        ),
      }),
    );
    if (
      orgConfigs?.botVerticals &&
      Array.isArray(orgConfigs?.botVerticals) &&
      orgConfigs?.botVerticals.length > 0 &&
      orgConfigs?.botDesignations &&
      Array.isArray(orgConfigs?.botDesignations) &&
      orgConfigs?.botDesignations.length > 0
    ) {
      setCompletedStep((prev) => {
        return {
          ...prev,
          [1]: true,
        };
      });
    } else {
      setCompletedStep((prev) => {
        return {
          ...prev,
          [1]: false,
        };
      });
    }
    if (userDetails?.custom?.orgType !== 'demo_org') {
      if (methodologiesAvailable) {
        if (orgConfigs?.isGeneralAutoAnalysis !== null) {
          setCompletedStep((prev) => {
            return {
              ...prev,
              [3]: true,
            };
          });
          dispatch(
            updateOnboardingStepCompletionPercentage({
              key: 3,
              value: 25,
            }),
          );
        } else {
          setCompletedStep((prev) => {
            return {
              ...prev,
              [3]: false,
            };
          });
          dispatch(
            updateOnboardingStepCompletionPercentage({
              key: 3,
              value: 0,
            }),
          );
        }
      } else {
        if (orgConfigs?.isGeneralAutoAnalysis !== null) {
          setCompletedStep((prev) => {
            return {
              ...prev,
              [2]: true,
            };
          });
          dispatch(
            updateOnboardingStepCompletionPercentage({
              key: 2,
              value: 25,
            }),
          );
        } else {
          setCompletedStep((prev) => {
            return {
              ...prev,
              [2]: false,
            };
          });
          dispatch(
            updateOnboardingStepCompletionPercentage({
              key: 2,
              value: 0,
            }),
          );
        }
      }
    } else {
      setCompletedStep((prev) => {
        return {
          ...prev,
          [3]: true,
        };
      });
      dispatch(
        updateOnboardingStepCompletionPercentage({
          key: 3,
          value: 0,
        }),
      );
    }
    if (methodologiesAvailable) {
      if (
        orgConfigs?.selectedMethodologies &&
        Array.isArray(orgConfigs?.selectedMethodologies) &&
        orgConfigs?.selectedMethodologies?.length > 0
      ) {
        setCompletedStep((prev) => {
          return {
            ...prev,
            [2]: true,
          };
        });
        dispatch(
          updateOnboardingStepCompletionPercentage({
            key: 2,
            value: 25,
          }),
        );
      } else {
        setCompletedStep((prev) => {
          return {
            ...prev,
            [2]: false,
          };
        });
        dispatch(
          updateOnboardingStepCompletionPercentage({
            key: 2,
            value: 0,
          }),
        );
      }
    }
  }, [orgConfigs]);

  // useEffect(() => {
  //   if (onBoardingStep === 1 || onBoardingStep === 2) {
  //     dispatch(fetchPlaybookHistory(teamId || null))
  //       .unwrap()
  //       .then((data) => {
  //         if (data?.selectedPlaybook) {
  //           setCompletedStep((prev) => {
  //             return {
  //               ...prev,
  //               [0]: true,
  //             };
  //           });
  //           dispatch(
  //             updateOnboardingStepCompletionPercentage({
  //               key: 0,
  //               value: userDetails?.custom?.orgType === 'demo_org' ? 50 : 25,
  //             }),
  //           );
  //         } else {
  //           setCompletedStep((prev) => {
  //             return {
  //               ...prev,
  //               [0]: false,
  //             };
  //           });
  //           dispatch(
  //             updateOnboardingStepCompletionPercentage({
  //               key: 0,
  //               value: 0,
  //             }),
  //           );
  //         }
  //       })
  //       .catch(() => {});
  //   }
  // }, [onBoardingStep]);

  // Mirrors the org config into ui state, and -- the reason this effect is
  // load-bearing for boot -- resolves `showOnboardingDialogV2` out of its
  // `null` "unknown" start.
  //
  // The flag used to default to `true`, so the fullscreen wizard mounted and
  // painted its own 40px spinner over an already-rendered app on every load,
  // closing again only once this config proved onboarding was finished. It now
  // opens only on proof of the opposite: a resolved stage that is non-empty and
  // is not `onboarding_completed`. A retired stage is resolved through
  // `RETIRED_STAGE_REDIRECTS` first, so one that redirects to
  // `onboarding_completed` does not open the wizard.
  //
  // Opening is one-way, and only out of `null`. `fetchOrganizationConfigs` is
  // dispatched from ~67 sites, so this effect re-runs throughout the session;
  // once the flag has settled either way, a later config that still reports an
  // incomplete stage must not re-open a wizard the user has finished or closed.
  // Closing stays unconditional -- that is the transition the old default-open
  // flag made, and the only one it ever made.
  //
  // A failed config fetch settles the flag to `false` rather than leaving it
  // `null`: consumers hold their own behaviour open while it is unknown, and a
  // request that is never going to succeed must not hold them forever.
  useEffect(() => {
    if (orgConfigs) {
      dispatch(
        updateUiState({
          key: 'onboardingStage',
          value: orgConfigs?.onboardingStage || '',
        }),
      );
      dispatch(
        updateUiState({
          key: 'experimentalFeatureEnabled',
          value: orgConfigs?.experimentalFeaturesEnabled || false,
        }),
      );
      if (!isOnboardingIncomplete(orgConfigs?.onboardingStage)) {
        dispatch(
          updateUiState({
            key: 'showOnboardingDialogV2',
            value: false,
          }),
        );
      } else if (showOnboardingDialogV2Ref.current === null) {
        dispatch(
          updateUiState({
            key: 'showOnboardingDialogV2',
            value: true,
          }),
        );
      }
    } else if (orgConfigsFailed) {
      dispatch(
        updateUiState({
          key: 'showOnboardingDialogV2',
          value: false,
        }),
      );
    }
  }, [orgConfigs, orgConfigsFailed]);

  useEffect(() => {
    dispatch(fetchOrganizationConfigs());
    dispatch(getInteractionCounts());
    // dispatch(fetchPlaybookHistory(teamId || null))
    //   .unwrap()
    //   .then((data) => {
    //     if (data?.selectedPlaybook) {
    //       setCompletedStep((prev) => {
    //         return {
    //           ...prev,
    //           [0]: true,
    //         };
    //       });
    //       dispatch(
    //         updateOnboardingStepCompletionPercentage({
    //           key: 0,
    //           value: userDetails?.custom?.orgType === 'demo_org' ? 50 : 25,
    //         }),
    //       );
    //     } else {
    //       setCompletedStep((prev) => {
    //         return {
    //           ...prev,
    //           [0]: false,
    //         };
    //       });
    //       dispatch(
    //         updateOnboardingStepCompletionPercentage({
    //           key: 0,
    //           value: 0,
    //         }),
    //       );
    //     }
    //   })
    //   .catch(() => {});
  }, []);
};

export default useOnboardingProgress;
